import React, { Reducer, useReducer, useRef } from 'react';
import { calculateNextElementsIndex, setElementFocus } from '../../../lib/DomUtils/utils';
import { ID } from '../../../types/api/ID';
import { KeyEvent } from '../../../types/KeyEvent';

type useAccordionOptions<TAction> = {
  initialActiveElementId: string | null;
  reducer: Reducer<string[], TAction>;
};

function defaultReducer<TAction extends ToggleAction>(state: string[], action: TAction) {
  switch (action.type) {
    case 'toggle':
      return state.includes(action.id) ? [] : [action.id];
    default:
      return state;
  }
}

export type ToggleAction = {
  type: 'toggle';
  id: string;
};

export function useAccordion<TItem extends ID, TAction>(
  data: TItem[],
  options: Partial<useAccordionOptions<TAction>> = {},
) {
  const { initialActiveElementId } = options;
  const [activeElementIds, dispatch] = useReducer(
    options.reducer || defaultReducer as any,
    initialActiveElementId ? [initialActiveElementId] : [],
  );

  const accordionRef = useRef<HTMLDivElement>(null);

  const toggleActiveElement = (elementId: string) => dispatch({ type: 'toggle', id: elementId });

  const keyboardNavigation: React.KeyboardEventHandler<HTMLButtonElement> = (event) => {
    if (!accordionRef.current) {
      return;
    }
    if (event.code === KeyEvent.Home) {
      event.preventDefault();
      setElementFocus(accordionRef.current, 0, event.currentTarget.className);
    }

    if (event.code === KeyEvent.End) {
      event.preventDefault();
      setElementFocus(accordionRef.current, accordionRef.current.children.length - 1, event.currentTarget.className);
    }

    if (
      event.code === KeyEvent.ArrowUp ||
      event.code === KeyEvent.ArrowDown ||
      (event.ctrlKey && (event.code === KeyEvent.PageUp || event.code === KeyEvent.PageDown))
    ) {
      event.preventDefault();

      if (Array.isArray(data)) {
        setElementFocus(
          accordionRef.current,
          calculateNextElementsIndex(
            accordionRef.current,
            event.currentTarget.id,
            event.code === KeyEvent.ArrowDown || event.code === KeyEvent.PageDown ? 1 : -1,
          ),
          event.currentTarget.className,
        );
      }
    }
  };

  const items = data.map((item) => {
    const panelId = `accordion-panel-${item.id}`;
    const headerId = `accordion-item-${item.id}`;
    const buttonId = `accordion-button-${item.id}`;
    const isActiveElement = activeElementIds.includes(item.id);

    return {
      ...item,
      panelId,
      getToggleButtonProps: () => ({
        onKeyDown: keyboardNavigation,
        onClick: () => toggleActiveElement(item.id),
        key: `AccordionItem-${item.id}`,
        'aria-expanded': isActiveElement,
        'aria-controls': panelId,
        id: buttonId,
      }),
      getPanelProps: () => ({
        id: panelId,
        'aria-hidden': !isActiveElement,
        'aria-labelledby': buttonId,
      }),
      getElementProps: () => ({
        id: headerId,
      }),
      isActiveElement,
    };
  });

  const getAccordionProps = () => ({ ref: accordionRef });

  return { getAccordionProps, activeElementIds, items, dispatch };
}
